import { SubmissionError } from 'redux-form'
import { rest, isApiError } from 'services'
import isEmpty from 'lodash/isEmpty'
import dayjs from 'dayjs'
import { show } from 'redux-modal'
import { push } from 'connected-react-router'
import { isErrorCode, isErrorDescription } from 'helpers/utils'
import { sendServicesAndDateTime } from 'components/Appointment/actions'
import { getShoppingData, cleanShoppingData, setShoppingData } from 'actions/shoppingData'
import { setPreviousOrder } from 'actions/previousOrder'
import { preparePhoneNumber } from 'helpers/phone-number'
import {
  CODE_APPOINTMENT_AVAILABILITY_ERROR,
  CODE_QUICK_APPT_SUBMISSION_ERROR,
  DESC_PLEASE_SELECT_STORE,
  COMPATIBILITY_STATUS_NON_COMPATIBLE,
  GENERIC_MESSAGE_MODAL } from 'constant'
import { APPOINMENT_SUBMISSION_ERROR_MODAL } from 'components/QuickAppointmentPage/Stepper/constants'
import { googleAnalyticPurchase, googleAnalyticQuickApptSubmissionError } from 'actions/googleAnalytic'
import { getAppointmentConfirmationFormValues, getAppointmentFormValues } from './selectors'
import { setAppointmentResponse } from 'components/QuickAppointmentPage/actions'
import { APPOINTMENT_CHANGE_MODAL, APPOINTMENT_SUCCESS_MODAL } from './../constants'
import {
  REMOVE_INACTIVE_TITLE,
  REMOVE_INACTIVE_MESSAGE,
  REMOVE_INCOMPATIBLE_MESSAGE
} from './../../Appointment/constants'
import IncompatibleItemsSubcomponent from './../../Appointment/components/IncompatibleItemsSubcomponent'
import DiscontinuedItemInQuoteSubComponent from 'components/Appointment/components/DiscontinuedItemInQuoteSubComponent'
import RequestBuilder from 'services/microServices/requestBuilder'
import apiEndPoints from 'services/microServices/endPoints'
import { createAppointmentSchedulingPayload } from './helpers'
import { setUpdatingBlock } from 'components/common/UpdatingBlock/actions.js'
import { APPOINTMENT_SCHEDULING_IN_PROGRESS } from 'components/common/UpdatingBlock/constants.js'
import { getActiveVehicleId } from 'selectors/getActiveVehicle'
import { resetCalendarStartDate, resetProductsCount, resetSkusAddedToCart } from '../../common/hoc/withAddToQuote/actions'
import { getCalendarStartDate } from 'components/common/hoc/withAddToQuote/selectors'
import { getUserData } from 'components/Appointment/selectors'

const { api } = rest
const { appointmentSchedulingService: { url, method, basePath }} = apiEndPoints

const navigateToOrderConfirmation = () => push('/appointment/orderConfirmation')

const handleAppointmentConfirmationForPilotedStore = (dispatch, state) => {
  const { schedulingAppointmentPayload, orderConfirmationPayload } = createAppointmentSchedulingPayload(state)
  const { shoppingData } = state

  dispatch(setUpdatingBlock({ name: APPOINTMENT_SCHEDULING_IN_PROGRESS, value: true }))
  return new RequestBuilder(url, basePath)
    .withMethod(method)
    .withData(schedulingAppointmentPayload)
    .withSuccessHandler((response) => {
      if (!isEmpty(response) && response.data) {
        const { data: { poNumber }} = response
        dispatch(setUpdatingBlock({ name: APPOINTMENT_SCHEDULING_IN_PROGRESS, value: false }))
        dispatch(setPreviousOrder({ shoppingData, appointmentData: { poNumber, ...orderConfirmationPayload }}))
        dispatch(navigateToOrderConfirmation())
      }
    })
    .withErrorHandler((error) => {
      dispatch(show(APPOINMENT_SUBMISSION_ERROR_MODAL))
      dispatch(setUpdatingBlock({ name: APPOINTMENT_SCHEDULING_IN_PROGRESS, value: false }))
    })
    .execute()
}


const handleAppointmentConfirmationForNonPilotedStore = (dispatch, state, payload = {}) => {
  const { appointmentDate: { date, time } = {}} = getAppointmentFormValues(state) || {}
  const {
    phoneNumber,
    firstName,
    lastName,
    userPlan,
    email,
    promonOffers,
    termsConditions
  } = getAppointmentConfirmationFormValues(state) || {}
  const selectedVehicleId = getActiveVehicleId(state)
  const availableDate = getCalendarStartDate(state) || undefined
  const appointmentData = {
    appointmentDateText: dayjs(date).format('MM/DD/YYYY'),
    appointmentTimeText: time?.value,
    firstName,
    lastName,
    userPlan,
    email,
    promonOffers,
    termsConditions,
    availableDate,
    ...preparePhoneNumber(phoneNumber),
    ...payload
  }
  const { shoppingData } = state
  const { products, services } = shoppingData.quotedItemAndServices

  if (products && products.length) {
    const hasInactiveItems = products.some(product => !product.isActive)

    if (hasInactiveItems) {
      const inactiveItemsModalOptions = {
        title: REMOVE_INACTIVE_TITLE,
        text: REMOVE_INACTIVE_MESSAGE,
        SubComponents: DiscontinuedItemInQuoteSubComponent
      }

      dispatch(show(GENERIC_MESSAGE_MODAL, inactiveItemsModalOptions))
      return
    }

    const hasIncompatibleItems = products.some(({
      compatibilityStatus: { compatibilityType } = {}
    }) => compatibilityType === COMPATIBILITY_STATUS_NON_COMPATIBLE)
    if (hasIncompatibleItems) {
      const incompatibleItemsModalOptions = {
        title: REMOVE_INACTIVE_TITLE,
        text: REMOVE_INCOMPATIBLE_MESSAGE,
        SubComponents: IncompatibleItemsSubcomponent
      }

      dispatch(show(GENERIC_MESSAGE_MODAL, incompatibleItemsModalOptions))
      return
    }
  }

  const profilePopulationData = { primaryPhone: phoneNumber, firstName, lastName, email }

  return dispatch(getShoppingData(rest.api.confirmSinglePageQuoteAppointment, appointmentData))
    .then(data => {
      const { blockedUser } = data // We are using attribute to not make /ScheduleAppointment API call for Users from
      // blocked emails list with active Pilot store. See BOT-4634 for details.

      // check is added to only call all these in case of non-pilot-stores
      // Additional added check for case when User has pilot store, no products, and one or more services in the cart
      // to avoid call of the /scheduleAppointment API call, due to its require tires as a mandatory parameter
      if (isEmpty(payload) || (!isEmpty(payload) && isEmpty(products) && !isEmpty(services)) || blockedUser) {
        // send GTM statistic. Should be called before cleanShoppingData
        dispatch(googleAnalyticPurchase(null))
        dispatch(setAppointmentResponse(data))
        dispatch(show(APPOINTMENT_SUCCESS_MODAL, { profilePopulationData }))
      } else {
        // to remove the skus from localstorage for whom the appointment has been made for pilot stores.
        const skusWithAvailability = JSON.parse(localStorage.getItem('skusWithPreviousAvailability'))
        const filteredSkusWithAvailability = skusWithAvailability?.filter(
          sku => sku.activeVehicleId !== selectedVehicleId
        )
        localStorage.setItem('skusWithPreviousAvailability',
          JSON.stringify([...filteredSkusWithAvailability])
        )

        handleAppointmentConfirmationForPilotedStore(dispatch, state)
      }

      return data
    })
    .then((data) => {
      dispatch(cleanShoppingData())
      dispatch(resetCalendarStartDate())
      dispatch(resetSkusAddedToCart())
      dispatch(resetProductsCount())
      dispatch(setShoppingData({ totalCount: data.totalCount }))
    })
    .catch(err => {
      if (isApiError(err)) {
        // check is added to only call all these in case of non-pilot-stores
        if (isEmpty(payload)) {
          if (isErrorCode({ err, code: CODE_APPOINTMENT_AVAILABILITY_ERROR })) {
            const { status = []} = err
            const { description: message } = status
            dispatch(show(APPOINTMENT_CHANGE_MODAL, { message }))
          } else if (isErrorCode({ err, code: CODE_QUICK_APPT_SUBMISSION_ERROR })) {
            dispatch(googleAnalyticQuickApptSubmissionError())
            dispatch(show(APPOINMENT_SUBMISSION_ERROR_MODAL))
          } else if (isErrorDescription({ err, description: DESC_PLEASE_SELECT_STORE })) {
            dispatch(show(APPOINMENT_SUBMISSION_ERROR_MODAL))
          } else {
            throw new SubmissionError({ _error: err.status.description })
          }
        }
      }
    })
}

export const submitAppointmentRequest = () => (dispatch, getState) => {
  const state = getState()
  const { isPilotStore } = getUserData(state)
  const payload = isPilotStore ? { isPilotStore: true } : {}

  handleAppointmentConfirmationForNonPilotedStore(dispatch, state, { ...payload })
}

export const getAppointmentDatesAndSlots = (setAppointmentDetailsResponseVO, setIsLoading) => dispatch => {
  setIsLoading(true)
  dispatch(getShoppingData(api.getTAOSAppointmentDatesAndSlots, { isFromOnlineAppointment: false }))
    .then(({ appointmentDetailsResponseVO = {}}) => setAppointmentDetailsResponseVO(appointmentDetailsResponseVO))
    .then(() => setIsLoading(false))
    .catch(e => {
      console.error(e)
      setIsLoading(false)
    })
}

export const submitAppointmentInfoForm = ({ phoneNumber, ...values }) => dispatch =>
  dispatch(sendServicesAndDateTime(false))
    .then(() => dispatch(getShoppingData(rest.api.changeAppointmentContactDetails, {
      ...values,
      ...preparePhoneNumber(phoneNumber),
      isFromOnlineAppointment: false
    })))
    .catch(err => {
      if (isApiError(err)) {
        throw new SubmissionError({ _error: err.status.description })
      }
    })

